/**
 * Treść klauzuli informacyjnej RODO / Polityki Prywatności
 *
 * Single source of truth — używany przez:
 *   - src/app/rodo/page.tsx (strona /rodo)
 *   - src/app/api/rodo-data/route.ts (fallback gdy brak danych w bazie)
 *   - src/app/event/[id]/page.tsx (modal w formularzu rejestracji)
 *
 * Aktualizacja treści w bazie danych: sql/update_rodo_content.sql
 */

export interface RodoSection {
  id: number;
  title: string;
  content: string;
}

/**
 * Pełna klauzula informacyjna podzielona na sekcje
 * Zgodna z art. 13 RODO oraz wymogami polskiego prawa
 */
export const RODO_SECTIONS: RodoSection[] = [
  {
    id: 1,
    title: '1. Administrator danych osobowych',
    content: `
<p>Administratorem Państwa danych osobowych jest <strong>Ośrodek Szkolenia Morskiego LIBRA</strong> z siedzibą przy ul. Portowej 37, 78-100 Kołobrzeg (dalej: „Administrator" lub „Ośrodek").</p>
<p>Kontakt z Administratorem jest możliwy:</p>
<ul>
  <li>telefonicznie pod numerem <a href="tel:+48943554745">94 355 47 45</a> lub <a href="tel:+48600909200">600 909 200</a>,</li>
  <li>drogą elektroniczną na adres <a href="mailto:info@szkoleniamorskie.pl">info@szkoleniamorskie.pl</a>,</li>
  <li>listownie na adres siedziby wskazany powyżej.</li>
</ul>
<p>Administrator nie wyznaczył Inspektora Ochrony Danych (IOD). We wszystkich sprawach dotyczących przetwarzania danych osobowych można kontaktować się bezpośrednio z Administratorem przy użyciu danych podanych powyżej.</p>
`.trim(),
  },
  {
    id: 2,
    title: '2. Cele i podstawy prawne przetwarzania danych',
    content: `
<p>Państwa dane osobowe przetwarzane są w następujących celach:</p>

<h4>a) Obsługa zgłoszenia na szkolenie (formularz online)</h4>
<ul>
  <li><strong>Zakres danych:</strong> imię, nazwisko, adres e-mail, numer telefonu, treść korespondencji.</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. b RODO — działania zmierzające do zawarcia umowy o świadczenie usługi szkoleniowej.</li>
  <li><strong>Cel:</strong> kontakt zwrotny, potwierdzenie zgłoszenia, ustalenie warunków uczestnictwa.</li>
</ul>

<h4>b) Realizacja umowy szkoleniowej i wydanie zaświadczeń</h4>
<ul>
  <li><strong>Zakres danych:</strong> imię (imiona), nazwisko, płeć, data i miejsce urodzenia, adres e-mail, numer telefonu.</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. b RODO (wykonanie umowy) oraz art. 6 ust. 1 lit. c RODO (obowiązek prawny) — w szczególności przepisy <em>ustawy z dnia 18 sierpnia 2011 r. o bezpieczeństwie morskim</em>, <em>Międzynarodowej Konwencji STCW</em> oraz rozporządzeń wykonawczych Ministra właściwego ds. gospodarki morskiej dotyczących szkoleń i ewidencji uczestników kursów morskich.</li>
  <li><strong>Cel:</strong> rejestracja uczestników, prowadzenie ewidencji szkoleń, wystawianie zaświadczeń i świadectw przeszkolenia, obsługa duplikatów.</li>
</ul>

<h4>c) Obsługa zapytań przez formularz kontaktowy</h4>
<ul>
  <li><strong>Zakres danych:</strong> imię i nazwisko, adres e-mail, numer telefonu (opcjonalnie), treść wiadomości.</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes Administratora polegający na udzielaniu odpowiedzi na zapytania kierowane do Ośrodka.</li>
</ul>

<h4>d) Wystawianie i przechowywanie dokumentów księgowych</h4>
<ul>
  <li><strong>Zakres danych:</strong> dane identyfikujące nabywcę usługi (imię, nazwisko lub firma, adres, NIP w przypadku faktury).</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. c RODO w związku z przepisami <em>ustawy z dnia 29 września 1994 r. o rachunkowości</em> oraz <em>ustawy z dnia 11 marca 2004 r. o podatku od towarów i usług</em>.</li>
</ul>

<h4>e) Ochrona przed nadużyciami (reCAPTCHA)</h4>
<ul>
  <li><strong>Zakres danych:</strong> adres IP, identyfikator przeglądarki, ciasteczka analityczne dostarczane przez Google.</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes Administratora polegający na zabezpieczeniu formularzy przed atakami botów i nadużyciami.</li>
</ul>

<h4>f) Statystyki odwiedzin strony (Google Analytics — opcjonalnie)</h4>
<ul>
  <li><strong>Zakres danych:</strong> zanonimizowany adres IP, identyfikator przeglądarki, dane o sesji, statystyki ruchu na stronie.</li>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. a RODO — Państwa dobrowolna zgoda wyrażona poprzez ustawienia plików cookies.</li>
  <li><strong>Cel:</strong> analiza ruchu na stronie i ulepszanie jej zawartości.</li>
  <li>Brak zgody nie skutkuje żadnymi konsekwencjami — strona pozostaje w pełni funkcjonalna.</li>
</ul>

<h4>g) Ustalanie, dochodzenie lub obrona przed roszczeniami</h4>
<ul>
  <li><strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes Administratora.</li>
</ul>
`.trim(),
  },
  {
    id: 3,
    title: '3. Odbiorcy danych osobowych',
    content: `
<p>Państwa dane osobowe mogą być przekazywane następującym kategoriom odbiorców:</p>
<ul>
  <li><strong>Organom administracji publicznej</strong> — w szczególności Urzędom Morskim, Ministerstwu właściwemu ds. gospodarki morskiej oraz innym organom uprawnionym do otrzymania danych na podstawie przepisów prawa (w zakresie realizacji obowiązków wynikających z prowadzenia szkoleń morskich).</li>
  <li><strong>Urzędowi Skarbowemu</strong> — w zakresie obowiązków podatkowo-księgowych.</li>
  <li><strong>Dostawcom usług IT i hostingowych</strong> — podmiotom świadczącym usługi utrzymania strony internetowej, serwerów oraz poczty elektronicznej (na podstawie umów powierzenia przetwarzania danych, art. 28 RODO).</li>
  <li><strong>Google Ireland Limited</strong> — dostawcy usług reCAPTCHA Enterprise (zabezpieczenie formularzy) oraz Google Analytics (gdy wyrażono zgodę na cookies analityczne).</li>
  <li><strong>Biuru rachunkowemu</strong> obsługującemu Administratora — w zakresie niezbędnym do prowadzenia rozliczeń.</li>
  <li><strong>Kancelariom prawnym</strong> oraz <strong>firmom windykacyjnym</strong> — wyłącznie w przypadku dochodzenia lub obrony przed roszczeniami.</li>
</ul>
<p>Dane nie są sprzedawane ani udostępniane podmiotom trzecim do celów marketingowych.</p>
`.trim(),
  },
  {
    id: 4,
    title: '4. Przekazywanie danych poza Europejski Obszar Gospodarczy (EOG)',
    content: `
<p>W związku z korzystaniem z usług Google (reCAPTCHA oraz opcjonalnie Google Analytics) Państwa dane mogą być przekazywane do <strong>Stanów Zjednoczonych Ameryki</strong>. Przekazanie odbywa się na podstawie:</p>
<ul>
  <li><strong>Decyzji wykonawczej Komisji Europejskiej (UE) 2023/1795 z dnia 10 lipca 2023 r.</strong> — Ramy Ochrony Danych UE–USA (EU-U.S. Data Privacy Framework), uznającej odpowiedni poziom ochrony danych w USA w odniesieniu do certyfikowanych podmiotów (Google LLC jest podmiotem certyfikowanym),</li>
  <li>pomocniczo — <strong>standardowych klauzul umownych</strong> zatwierdzonych przez Komisję Europejską (art. 46 ust. 2 lit. c RODO).</li>
</ul>
<p>Mogą Państwo uzyskać kopię stosowanych zabezpieczeń, kontaktując się z Administratorem.</p>
`.trim(),
  },
  {
    id: 5,
    title: '5. Okres przechowywania danych',
    content: `
<p>Państwa dane osobowe będą przechowywane przez następujące okresy:</p>
<ul>
  <li><strong>Dane uczestników szkoleń morskich</strong> (imię, nazwisko, data i miejsce urodzenia, płeć, dane kontaktowe, informacja o ukończonych kursach): <strong>przez okres 50 lat</strong> od ukończenia szkolenia — zgodnie z wymogami przepisów dotyczących dokumentacji szkoleniowej w gospodarce morskiej (Konwencja STCW, krajowe przepisy wykonawcze) oraz w celu umożliwienia wydania duplikatów zaświadczeń i świadectw przeszkolenia.</li>
  <li><strong>Zgłoszenia przez formularz rejestracji online</strong> (e-mail, numer telefonu, IP, dane techniczne): <strong>przez 12 miesięcy</strong> od daty zgłoszenia, chyba że doszło do zawarcia umowy — wówczas dane stają się częścią dokumentacji szkoleniowej i są przechowywane jak w pkt powyżej.</li>
  <li><strong>Korespondencja z formularza kontaktowego</strong>: <strong>do 24 miesięcy</strong> od ostatniego kontaktu, chyba że krótszy okres wynika z natury zapytania.</li>
  <li><strong>Dokumenty księgowe</strong> (faktury, paragony): <strong>przez 5 lat</strong> licząc od końca roku kalendarzowego, w którym upłynął termin płatności podatku (art. 86 § 1 Ordynacji podatkowej).</li>
  <li><strong>Dane przetwarzane na podstawie zgody</strong> (np. cookies analityczne): <strong>do czasu wycofania zgody</strong> lub przez maksymalnie 26 miesięcy w przypadku Google Analytics.</li>
  <li><strong>Dane przetwarzane w celu obrony przed roszczeniami</strong>: <strong>do upływu okresu przedawnienia roszczeń</strong> (co do zasady 6 lat, dla roszczeń związanych z prowadzeniem działalności gospodarczej — 3 lata).</li>
</ul>
`.trim(),
  },
  {
    id: 6,
    title: '6. Państwa prawa',
    content: `
<p>W związku z przetwarzaniem danych osobowych przysługują Państwu następujące prawa:</p>
<ul>
  <li><strong>Prawo dostępu</strong> do swoich danych (art. 15 RODO) oraz uzyskania ich kopii;</li>
  <li><strong>Prawo do sprostowania</strong> danych nieprawidłowych lub uzupełnienia niekompletnych (art. 16 RODO);</li>
  <li><strong>Prawo do usunięcia danych</strong> („prawo do bycia zapomnianym", art. 17 RODO) — z zastrzeżeniem, że nie znajduje ono zastosowania m.in. w zakresie, w jakim Administrator ma obowiązek prawny przechowywania danych (np. dokumentacja szkoleniowa, dokumenty księgowe);</li>
  <li><strong>Prawo do ograniczenia przetwarzania</strong> (art. 18 RODO);</li>
  <li><strong>Prawo do przenoszenia danych</strong> (art. 20 RODO) — w odniesieniu do danych przetwarzanych na podstawie umowy lub zgody;</li>
  <li><strong>Prawo wniesienia sprzeciwu</strong> wobec przetwarzania danych na podstawie prawnie uzasadnionego interesu Administratora (art. 21 RODO);</li>
  <li><strong>Prawo do wycofania zgody</strong> w dowolnym momencie — w zakresie danych przetwarzanych na podstawie zgody (art. 7 ust. 3 RODO); wycofanie zgody nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej wycofaniem;</li>
  <li><strong>Prawo do wniesienia skargi</strong> do organu nadzorczego — <strong>Prezesa Urzędu Ochrony Danych Osobowych</strong>, ul. Stawki 2, 00-193 Warszawa, jeżeli uznają Państwo, że przetwarzanie danych narusza przepisy RODO.</li>
</ul>
<p>W celu skorzystania z powyższych praw prosimy o kontakt z Administratorem (dane w sekcji 1).</p>
`.trim(),
  },
  {
    id: 7,
    title: '7. Dobrowolność podania danych',
    content: `
<p>Podanie danych osobowych jest:</p>
<ul>
  <li><strong>Dobrowolne</strong> — w zakresie korzystania z formularza kontaktowego oraz wyrażenia zgody na cookies analityczne. Brak podania danych w formularzu kontaktowym uniemożliwi udzielenie odpowiedzi na zapytanie.</li>
  <li><strong>Wymagane do zawarcia i realizacji umowy</strong> — w zakresie zgłoszenia uczestnictwa w kursie. Brak podania danych uniemożliwi zapis na szkolenie.</li>
  <li><strong>Wymogiem ustawowym</strong> — w zakresie danych niezbędnych do prowadzenia dokumentacji szkoleniowej zgodnie z przepisami o szkoleniu morskim oraz wystawiania dokumentów księgowych. Brak podania tych danych uniemożliwi wystawienie zaświadczenia o ukończeniu kursu lub faktury.</li>
</ul>
`.trim(),
  },
  {
    id: 8,
    title: '8. Profilowanie i zautomatyzowane podejmowanie decyzji',
    content: `
<p>Administrator <strong>nie podejmuje wobec Państwa zautomatyzowanych decyzji</strong>, w tym w oparciu o profilowanie, które wywoływałyby skutki prawne lub w podobny sposób istotnie na Państwa wpływały (w rozumieniu art. 22 RODO).</p>
<p>Narzędzia statystyczne (Google Analytics) mogą tworzyć anonimowe profile zachowań użytkowników na stronie wyłącznie w celach analitycznych — i wyłącznie po uprzedniej zgodzie.</p>
`.trim(),
  },
  {
    id: 9,
    title: '9. Pliki cookies',
    content: `
<p>Strona <strong>szkoleniamorskie.pl</strong> wykorzystuje pliki cookies (ciasteczka) podzielone na trzy kategorie:</p>

<h4>Cookies niezbędne (zawsze aktywne)</h4>
<p>Wymagane do prawidłowego działania strony — m.in. obsługa sesji, zapamiętanie preferencji dotyczących plików cookies, ochrona formularzy przez Google reCAPTCHA. Ich wyłączenie może spowodować niepoprawne działanie strony. <strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. f RODO (uzasadniony interes — działanie strony).</p>

<h4>Cookies analityczne (wymagają zgody)</h4>
<p>Google Analytics — zbieranie zanonimizowanych statystyk dotyczących ruchu i sposobu korzystania ze strony. Pomagają nam ulepszać zawartość. <strong>Podstawa prawna:</strong> art. 6 ust. 1 lit. a RODO (zgoda).</p>

<h4>Cookies marketingowe (wymagają zgody)</h4>
<p>Obecnie nie używamy plików cookies marketingowych. W przypadku ich wdrożenia w przyszłości będą one aktywowane wyłącznie po wyrażeniu odrębnej zgody.</p>

<h4>Zarządzanie zgodą</h4>
<p>Zgodę na cookies analityczne i marketingowe można w każdej chwili wyrazić lub wycofać poprzez ustawienia plików cookies dostępne na stronie (banner przy pierwszej wizycie oraz odpowiednia opcja w przyszłości w stopce). Można również usunąć cookies bezpośrednio z poziomu przeglądarki internetowej.</p>
`.trim(),
  },
  {
    id: 10,
    title: '10. Zmiany Polityki Prywatności',
    content: `
<p>Administrator może zmieniać treść niniejszej Polityki Prywatności w przypadku zmian w przepisach prawa, zakresie świadczonych usług lub stosowanych technologiach. Aktualna wersja Polityki jest zawsze dostępna pod adresem <a href="/rodo">/rodo</a>.</p>
<p>Ostatnia aktualizacja: 19 maja 2026 r.</p>
`.trim(),
  },
];

/**
 * Domyślna pojedyncza sekcja (legacy fallback dla starego API zwracającego pojedynczy blok)
 * Łączy wszystkie sekcje w jeden HTML
 */
export const RODO_COMBINED_HTML = RODO_SECTIONS
  .map((s) => `<h3>${s.title}</h3>\n${s.content}`)
  .join('\n\n');

/**
 * Tytuł całej klauzuli
 */
export const RODO_TITLE = 'Polityka Prywatności i Klauzula Informacyjna RODO';
